/**
 * Compact phone header.
 *
 * Left slot is the hamburger on a top-level screen and a back arrow on a
 * drill-down, matching the approved artwork. The brand lockup is centred-left
 * and never competes with the hero for space.
 */

import React, { memo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { colors, sizing, space } from '../../theme';
import { BackIcon, MenuIcon } from '../common/Icon';
import { LanguageToggle } from './LanguageToggle';
import { Logo, TAGLINE, unbrokenPhrases } from '../common/Logo';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface HeaderProps {
  /** Which affordance appears on the left. */
  leading?: 'menu' | 'back' | 'none';
  onMenuPress?: () => void;
  onBackPress?: () => void;
  /** Optional right-hand action, e.g. a notifications bell. */
  trailing?: React.ReactNode;
  testID?: string;
}

export const Header = memo(function Header({
  leading = 'menu',
  onMenuPress,
  onBackPress,
  trailing,
  testID = 'app-header',
}: HeaderProps) {
  const t = useT();
  return (
    <View style={styles.header} testID={testID}>
      <View style={styles.leading}>
        {leading === 'menu' ? (
          <Pressable
            onPress={onMenuPress}
            accessibilityRole="button"
            accessibilityLabel={t('Open navigation menu')}
            style={[styles.iconButton, styles.iconButtonLeading]}
            testID="header-menu"
          >
            <MenuIcon />
          </Pressable>
        ) : leading === 'back' ? (
          <Pressable
            onPress={onBackPress}
            accessibilityRole="button"
            accessibilityLabel={t('Go back')}
            style={[styles.iconButton, styles.iconButtonLeading]}
            testID="header-back"
          >
            <BackIcon />
          </Pressable>
        ) : (
          <View style={styles.iconButton} />
        )}
      </View>

      <View style={styles.brand}>
        <Logo size={32} />
        <View style={styles.brandText}>
          {/* Aligned by the app's language, not the words': the name is Latin,
              so left alone it sat on the left in Urdu, away from its logo. */}
          <Text variant="brandName" numberOfLines={1} align="start">
            Meal4ALL
          </Text>
          {/* Two lines at most: on a 320pt phone the tagline is 12pt wider than
              the space beside the logo, and wrapping it fits the 56pt bar
              where a cut-off "A BLE…" would not. */}
          <Text variant="brandTagline" tone="secondary" numberOfLines={2}>{unbrokenPhrases(t(TAGLINE))}</Text>
        </View>
      </View>

      <View style={styles.trailing}>
        {trailing}
        <LanguageToggle />
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  header: {
    height: sizing.headerHeight,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.bg.surface,
    // The same gutter the body uses, so the bar's contents line up with the
    // cards under it rather than sitting 8pt further out on both sides.
    paddingHorizontal: space.lg,
  },
  leading: {
    width: sizing.touchTarget,
  },
  iconButton: {
    width: sizing.touchTarget,
    height: sizing.touchTarget,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /**
   * The glyph sits on the gutter, not in the middle of its target: a 44pt
   * box centred on a 16pt margin draws its arrow 30pt in, which reads as
   * indented against the cards below. The target keeps its full width.
   */
  iconButtonLeading: {
    alignItems: 'flex-start',
  },
  brand: {
    flex: 1,
    // Without this a flex child will not shrink below its own content, so on
    // a 320pt phone with two pills in the header the name ran under them.
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
  },
  brandText: {
    flexShrink: 1,
    minWidth: 0,
  },
  trailing: {
    minWidth: sizing.touchTarget,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: space.sm,
  },
});
